import { useCallback, useEffect, useRef, useState } from "react";
import useUser from "@/users/useUser";
import getUserProperties from "@/users/getUserProperties";
import useMetadataSearchQuery from "@/home/TeacherHomepage/services/useMetadataSearchQuery";
import { IStandard } from "@/search/Langston/types/IStandard";
import {
  IFetchStandards,
  IStandardsFetchParam,
} from "@/search/Langston/types/IFetchStandards";
import { checkForEqualContentIds } from "../StandardsSearchProvider.utils";
import { useStandardsQuery } from "./useStandardsQuery";

const defaultFetchStandards: IFetchStandards = {
  shouldFetch: false,
  fetchParams: {},
};

export const useStandardsFetch = (
  currentPage: number,
  currentNeedle: string,
  options?: {
    searchParams?: URLSearchParams;
    getSearchParamValues?: (key: string) => string[];
  },
) => {
  const { fetchParamsForQuery, standardsInQuery, searchParamsObj } =
    useStandardsQuery(options);

  const { data: user } = useUser();
  const { isStudent } = getUserProperties(user, { shouldUseNewUserApi: false });
  const canSeeStandardsFilter = !isStudent;
  const classroomGradeLevels = user?.classroom_grade_levels || [];

  const [fetchStandards, setFetchStandards] = useState<IFetchStandards>(
    defaultFetchStandards,
  );
  const [selectedStandards, setSelectedStandards] = useState<IStandard[]>([]);
  const [allStandardsModalResults, setAllStandardsModalResults] = useState<
    IStandard[]
  >([]);

  const temporarySelectedFilters = useRef<IStandard[]>([]);
  const firstPageStandards = useRef<IStandard[]>([]);
  const lastUpdatedModalResults = useRef<IStandard[]>([]);
  const lastUpdatedPage = useRef<number>(0);

  const fetchFilteredStandards = useCallback(
    (params?: IStandardsFetchParam) => {
      setFetchStandards({
        shouldFetch: true,
        fetchParams: {
          ...searchParamsObj,
          ...(params || {}),
        },
      });
    },
    [searchParamsObj],
  );

  const [queryParams, setQueryParams] = useState<any | null>(null);

  useEffect(() => {
    if (fetchStandards.shouldFetch) {
      setQueryParams({
        ...fetchStandards.fetchParams,
        classroomGradeLevels,
      });
      setFetchStandards(defaultFetchStandards);
    }
  }, [fetchStandards, classroomGradeLevels]);

  const {
    data: standardsModalData,
    isLoading: isLoadingStandardsModalResults,
  } = useMetadataSearchQuery({
    ...queryParams,
    skip: !queryParams,
  });

  const { data: top5StandardsData } = useMetadataSearchQuery({
    page_size: 5,
    classroomGradeLevels,
    skip: !canSeeStandardsFilter,
    ...searchParamsObj,
  });

  const { data: selectedStandardsData } = useMetadataSearchQuery({
    ...fetchParamsForQuery,
    classroomGradeLevels,
    skip: !fetchParamsForQuery,
  });

  useEffect(() => {
    if (
      isLoadingStandardsModalResults ||
      !standardsModalData?.results ||
      checkForEqualContentIds(
        standardsModalData.results,
        allStandardsModalResults,
      ) ||
      currentPage === lastUpdatedPage.current ||
      (checkForEqualContentIds(
        standardsModalData.results,
        lastUpdatedModalResults.current,
      ) &&
        currentNeedle === "")
    ) {
      return;
    }

    if (firstPageStandards.current.length === 0) {
      setAllStandardsModalResults([...standardsModalData.results]);
      firstPageStandards.current = [...standardsModalData.results];
    } else {
      setAllStandardsModalResults(
        currentPage === 1
          ? [...standardsModalData.results]
          : [...allStandardsModalResults, ...standardsModalData.results],
      );
      lastUpdatedModalResults.current = standardsModalData.results;
      if (currentPage > 1) {
        lastUpdatedPage.current = currentPage;
      }
    }

    if (currentPage === 1 && lastUpdatedPage.current > 1) {
      lastUpdatedPage.current = 0;
    }
  }, [standardsModalData?.results, currentNeedle]);

  useEffect(() => {
    if (
      selectedStandardsData?.results.length &&
      selectedStandardsData?.results.length === standardsInQuery.length
    ) {
      setSelectedStandards(selectedStandardsData.results);
      temporarySelectedFilters.current = [];
    }
  }, [selectedStandardsData, standardsInQuery]);

  const updateSelectedStandardsOnChange = (
    isChecked: boolean,
    standard: IStandard,
    updateNow: boolean = false,
  ) => {
    const current =
      temporarySelectedFilters.current.length > 0
        ? [...temporarySelectedFilters.current]
        : [...selectedStandards];

    const updated = isChecked
      ? [...current, standard]
      : current.filter((s) => s.id !== standard.id);

    temporarySelectedFilters.current = updated;

    if (updateNow) {
      setSelectedStandards(updated);
      temporarySelectedFilters.current = [];
    }
  };

  const resetStandardsList = () => {
    setAllStandardsModalResults(firstPageStandards.current);
  };

  const getTemporarySelectedFilters = () => temporarySelectedFilters.current;

  const clearTemporarySelectedFilters = () => {
    temporarySelectedFilters.current = [];
  };

  return {
    allStandardsModalResults,
    selectedStandards,
    updateSelectedStandardsOnChange,
    top5Standards: top5StandardsData?.results || [],
    totalStandardsResults: standardsModalData?.total_result_count || 0,
    isLoadingStandardsResults: isLoadingStandardsModalResults,
    fetchFilteredStandards,
    standardsInQuery,
    searchParamsObj,
    resetStandardsList,
    setSelectedStandards,
    getTemporarySelectedFilters,
    clearTemporarySelectedFilters,
  };
};
